// Membership and How creators earn (owner 2026-10-21, PLAN "Three tiers"; docs/build/membership.md). The copy lives in
// ../membership-copy.ts, shared with the launcher's ui/membership.rs. Memberships are Stripe Billing subscriptions
// (docs/backend/money.md "Memberships"), open exactly while money sells (`/v1/config` `membership.open`): then each paid
// tier gets a Join button (money's checkout, then Stripe's hosted page) and members see their status and Manage (the
// portal). Otherwise the page says "coming later" with no button. Perks that don't exist yet say "Coming". The earn page
// shows money while selling is off too, framed as how it will work (claims.md P9/P10).
import { api, ApiError } from "../api.ts";
import { COPY } from "../membership-copy.ts";
import { clear, h, link } from "../dom.ts";
import { CURRENCIES, formatMoney, viewerCurrency, type Currency } from "../money.ts";
import { setTitle } from "../router.ts";
import { moneyConfig, selling } from "../selling.ts";
import { signedIn } from "../session.ts";

export interface Perk {
  text: string;
  live: boolean;
  /** live whenever memberships are open */
  with_billing?: boolean;
}
export interface Tier {
  id: string;
  name: string;
  price: string;
  who: string;
  perks: Perk[];
}

export const MEMBERSHIP = COPY.membership;
export const EARN = COPY.earn;
export const TIERS: Tier[] = COPY.membership.tiers;

const CSS = `
.mem { max-width: 1080px; margin: 0 auto; padding: 8px 0 48px; }
.mem .eyebrow { font-size: .75rem; letter-spacing: .14em; text-transform: uppercase; color: var(--ruby); font-weight: 700; }
.mem h1 { font-size: clamp(2rem, 4.4vw, 3.05rem); line-height: 1.08; letter-spacing: -.03em; font-weight: 850; margin: 10px 0 12px; }
.mem h2 { font-size: 1.35rem; margin: 36px 0 12px; }
.mem-hero { display: grid; grid-template-columns: 1.3fr 1fr; gap: 28px; align-items: center; background: radial-gradient(120% 140% at 0% 0%, #3a1424 0%, #1a1218 45%, #141418 100%); border: 1px solid var(--border); border-radius: 20px; padding: 32px 36px; }
.mem-hero .lead { margin: 0; }
.mem-closed { background: rgba(0,0,0,.35); border: 1px solid var(--border); border-radius: var(--radius); padding: 20px 22px; }
.mem-closed h3 { font-size: 1.6rem; margin: 0 0 6px; letter-spacing: -.02em; }
.mem-closed p { margin: 0; color: #c9c9d1; }
.mem-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
.mem-card { background: var(--panel); border: 1px solid var(--border); border-radius: var(--radius); padding: 18px 20px; }
.mem-card h3 { margin: 0 0 6px; font-size: 1rem; }
.mem-card p { margin: 0; color: var(--dim); font-size: .9rem; }
.mem-split { display: flex; height: 34px; border-radius: 9px; overflow: hidden; margin: 0 0 8px; font-size: .82rem; font-weight: 700; }
.mem-split div { display: flex; align-items: center; padding: 0 12px; white-space: nowrap; overflow: hidden; }
.mem-split .c { flex: 80; background: var(--ruby); color: #fff; }
.mem-split .b { flex: 20; background: var(--card); color: var(--dim); }
.mem-tier .price { font-size: 1.3rem; font-weight: 800; margin: 2px 0; }
.mem-tier .who { color: var(--faint); font-size: .85rem; margin-bottom: 12px; }
.mem-tier ul { list-style: none; padding: 0; margin: 0; display: grid; gap: 8px; font-size: .9rem; }
.mem-tier li { display: grid; grid-template-columns: 16px minmax(0, 1fr); gap: 8px; }
.mem-tier li::before { content: "✓"; color: var(--ruby); font-weight: 700; }
.mem-tier li.soon { color: var(--dim); }
.mem-tier li.soon::before { content: "·"; color: var(--faint); }
.mem-soon { font-size: .68rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: var(--faint); border: 1px solid var(--border); border-radius: 5px; padding: 0 5px; margin-left: 6px; white-space: nowrap; }
.mem-promise { color: var(--dim); margin-top: 16px; }
.mem-tier .mem-join { margin-top: 16px; width: 100%; justify-content: center; }
.mem-currency { display: flex; align-items: center; gap: 8px; margin: 12px 0 4px; }
.mem-currency .chip { border: 1px solid var(--border); background: transparent; color: var(--text); border-radius: 999px; padding: 2px 12px; font: inherit; font-size: .85rem; cursor: pointer; }
.mem-currency .chip[aria-pressed="true"] { background: var(--text); color: #111; }
.mem-note { min-height: 1.2em; color: var(--dim); font-size: .9rem; margin: 8px 0 0; }
.mem-closed .actions { margin: 12px 0 0; }
.mem-banner { background: #1d1a12; border: 1px solid #4a3d1c; color: #f3e2b0; border-radius: 12px; padding: 12px 16px; font-size: .9rem; margin-bottom: 22px; }
.mem table { width: 100%; border-collapse: collapse; font-size: .9rem; }
.mem th, .mem td { padding: 11px 12px; border-bottom: 1px solid var(--border); text-align: left; vertical-align: top; }
.mem th { color: var(--dim); font-weight: 600; }
.mem td.n { font-weight: 700; }
.mem .two { grid-template-columns: repeat(2, minmax(0, 1fr)); margin-top: 28px; }
.mem .note-line { color: var(--faint); font-size: .82rem; margin-top: 10px; }
@media (max-width: 760px) {
  .mem-hero, .mem-grid, .mem .two { grid-template-columns: minmax(0, 1fr); }
  .mem-hero { padding: 22px 18px; }
  .mem table, .mem tbody, .mem tr, .mem td { display: block; }
  .mem thead, .mem tr:first-child th { display: none; }
  .mem td { border: 0; padding: 2px 0; }
  .mem td[data-label]::before { content: attr(data-label) ": "; color: var(--dim); font-weight: 400; }
  .mem tr { border-bottom: 1px solid var(--border); padding: 10px 0; }
}
`;

function style(): void {
  if (!document.getElementById("mem-style")) document.head.appendChild(h("style", { id: "mem-style" }, CSS));
}

/** Memberships can be joined: money sells and says memberships are open. */
export function membershipOpen(isSelling: boolean, cfg: unknown): boolean {
  return isSelling && (cfg as { membership?: { open?: unknown } } | null)?.membership?.open === true;
}

export const perkLive = (p: Perk, open: boolean): boolean => p.live || (open && p.with_billing === true);

/** `{price}`-style placeholders. */
export const fill = (t: string, vars: Record<string, string>): string => t.replace(/\{(\w+)\}/g, (_, k: string) => vars[k] ?? "");

/** A tier's price in a currency from money's config (minor units), or null. */
export function tierPrice(cfg: unknown, tier: string, currency: Currency): number | null {
  const tiers = (cfg as { membership?: { tiers?: { id: string; prices: Record<string, { amount_minor: number }> }[] } } | null)?.membership?.tiers ?? [];
  const n = tiers.find((t) => t.id === tier)?.prices?.[currency]?.amount_minor;
  return typeof n === "number" ? n : null;
}

export interface MemberState {
  tier: "member" | "pro";
  status: "active" | "past_due" | "pending" | "canceled" | "abandoned";
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

/** What the status box says for a member (lines in order), from money's `/me/membership` `membership`. */
export function statusLines(m: MemberState, locale = "en-US"): string[] {
  const o = MEMBERSHIP.open;
  const name = m.tier === "pro" ? "Creator Pro member" : "Member";
  const date = m.current_period_end ? new Date(m.current_period_end).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" }) : "";
  const lines = [fill(o.active, { tier: name })];
  if (m.status === "past_due") lines.push(o.past_due);
  else if (date) lines.push(fill(m.cancel_at_period_end ? o.ends : o.renews, { date }));
  return lines;
}

function tierCard(t: Tier, open = false, join: HTMLElement | null = null): HTMLElement {
  return h(
    "div",
    { class: "mem-card mem-tier", "data-tier": t.id },
    h("h3", null, t.name),
    h("div", { class: "price" }, t.price),
    h("div", { class: "who" }, t.who),
    h(
      "ul",
      null,
      t.perks.map((p) => {
        const live = perkLive(p, open);
        return h("li", { class: live ? null : "soon" }, h("span", null, p.text, live ? null : h("span", { class: "mem-soon" }, MEMBERSHIP.coming)));
      }),
    ),
    join,
  );
}

/** The hero's box and the Join buttons while memberships are open. */
function openParts(): { box: HTMLElement; joins: Record<string, HTMLElement> } {
  const o = MEMBERSHIP.open;
  const cfg = moneyConfig();
  const box = h("div", { class: "mem-closed", "aria-live": "polite" });
  const joins: Record<string, HTMLElement> = {};
  let currency: Currency = viewerCurrency();
  const note = h("p", { class: "mem-note" });
  const buttons: [string, HTMLButtonElement][] = [];
  const label = () => {
    for (const [tier, b] of buttons) {
      const n = tierPrice(cfg, tier, currency);
      b.textContent = n === null ? o.title : fill(o.join, { price: formatMoney(n, currency) });
    }
  };
  const join = async (tier: string, b: HTMLButtonElement) => {
    for (const [, x] of buttons) x.disabled = true;
    note.textContent = o.opening;
    try {
      const r = await api<{ checkout_url: string; merchant: string }>("money", "/membership/checkout", { body: { tier, currency }, idempotent: true });
      // dev: the fake merchant's checkout is a POST that pays at once (DEV_MODE only); Stripe's is a page
      if (r.merchant === "fake") {
        await fetch(r.checkout_url, { method: "POST", mode: "no-cors", body: "{}" });
        location.assign("/membership?joined=1");
      } else location.assign(r.checkout_url);
    } catch (e) {
      note.textContent = e instanceof ApiError && e.code === "creator_age" ? o.pro_age : e instanceof ApiError && e.code === "already_member" ? e.message : o.error;
      for (const [, x] of buttons) x.disabled = false;
      b.focus();
    }
  };
  for (const t of TIERS.filter((t) => t.id !== "free")) {
    const b = h("button", { type: "button", class: "btn mem-join", "data-tier": t.id }) as HTMLButtonElement;
    b.addEventListener("click", () => void join(t.id, b));
    buttons.push([t.id, b]);
    joins[t.id] = signedIn() ? b : link(`/signin?next=${encodeURIComponent("/membership")}`, { class: "btn mem-join" }, o.sign_in);
  }
  label();
  const picker = h(
    "div",
    { class: "mem-currency", role: "group", "aria-label": o.pay_in },
    h("span", { class: "dim small" }, o.pay_in),
    CURRENCIES.map((c) => {
      const b = h("button", { type: "button", class: "chip", "aria-pressed": String(c === currency) }, c) as HTMLButtonElement;
      b.addEventListener("click", () => {
        currency = c;
        for (const x of picker.querySelectorAll("button")) x.setAttribute("aria-pressed", String(x.textContent === c));
        label();
      });
      return b;
    }),
  );
  const draw = (state: { membership: MemberState | null; pending: MemberState | null } | null) => {
    const live = state?.membership ?? null;
    for (const el of Object.values(joins)) el.hidden = !!live;
    if (live) {
      const manage = h("button", { type: "button", class: "btn ghost" }, o.manage) as HTMLButtonElement;
      manage.addEventListener("click", async () => {
        manage.disabled = true;
        try {
          location.assign((await api<{ url: string }>("money", "/membership/portal", { method: "POST" })).url);
        } catch {
          note.textContent = o.error;
          manage.disabled = false;
        }
      });
      clear(box, h("h3", null, statusLines(live)[0]), statusLines(live).slice(1).map((l) => h("p", null, l)), h("p", { class: "actions" }, manage), note);
    } else {
      clear(box, h("h3", null, o.title), state?.pending ? h("p", null, o.pending) : null, signedIn() ? picker : null, h("p", { class: "dim small" }, o.card_note), note);
    }
  };
  draw(null);
  if (signedIn()) {
    const load = () => api<{ membership: MemberState | null; pending: MemberState | null }>("money", "/me/membership");
    void load().then(async (st) => {
      draw(st);
      // back from Stripe: the webhook may land a moment after the buyer
      if (!st.membership && new URLSearchParams(location.search).has("joined")) {
        for (const wait of [1500, 3000, 6000]) {
          await new Promise((r) => setTimeout(r, wait));
          const next = await load().catch(() => null);
          if (next?.membership) return draw(next);
        }
      }
    }, () => draw(null));
  }
  return { box, joins };
}

export function membershipPage(): HTMLElement {
  setTitle("Membership");
  style();
  const m = MEMBERSHIP;
  const open = membershipOpen(selling(), moneyConfig());
  const parts = open ? openParts() : null;
  return h(
    "article",
    { class: "mem" },
    h(
      "section",
      { class: "mem-hero" },
      h("div", null, h("div", { class: "eyebrow" }, open ? m.open.eyebrow : m.eyebrow), h("h1", null, m.title), h("p", { class: "lead" }, m.lead)),
      parts ? parts.box : h("div", { class: "mem-closed" }, h("h3", null, m.closed.title), h("p", null, m.closed.body)),
    ),
    h("h2", null, m.shared_title),
    h("div", { class: "mem-grid" }, m.shared.map((c) => h("div", { class: "mem-card" }, h("h3", null, c.title), h("p", null, c.body)))),
    h(
      "div",
      { class: "mem-card", style: { marginTop: "14px" } },
      h("div", { class: "mem-split" }, h("div", { class: "c" }, m.split.creators), h("div", { class: "b" }, m.split.banger)),
      h("p", null, m.split.note),
    ),
    h("h2", null, m.tiers_title),
    h("div", { class: "mem-grid" }, TIERS.map((t) => tierCard(t, open, parts?.joins[t.id] ?? null))),
    h("p", { class: "mem-promise" }, m.promises, " ", link("/earn", null, m.earn_link)),
  );
}

export function earnPage(): HTMLElement {
  setTitle("How creators earn");
  style();
  const e = EARN;
  const on = selling();
  return h(
    "article",
    { class: "mem" },
    on ? null : h("div", { class: "mem-banner" }, e.banner),
    h("div", { class: "eyebrow" }, e.eyebrow),
    h("h1", null, e.title),
    h("p", { class: "lead" }, on ? e.lead_on : e.lead_off),
    h("h2", null, e.table_title),
    h(
      "table",
      null,
      h("thead", null, h("tr", null, e.head.map((c) => h("th", null, c)))),
      h("tbody", null, e.rows.map(([what, creator, banger]) => h("tr", null, h("td", null, what), h("td", { class: "n", "data-label": e.head[1] }, creator), h("td", { "data-label": e.head[2] }, banger)))),
    ),
    h("p", { class: "note-line" }, e.table_note),
    h(
      "div",
      { class: "mem-grid two" },
      h("div", { class: "mem-card" }, h("h3", null, e.balance.title), h("p", null, e.balance.body)),
      h("div", { class: "mem-card" }, h("h3", null, e.paid.title), h("p", null, e.paid.body), h("p", { style: { marginTop: "12px" } }, link("/dashboard", { class: "btn ghost small" }, e.dashboard))),
    ),
    h("p", { class: "mem-promise" }, link("/membership", null, e.membership_link)),
  );
}
